/**
 * ImageExportDialog - Map image export configuration dialog
 *
 * Provides a Material-UI dialog for configuring image export options:
 * - Format selection (PNG, JPG, SVG)
 * - Custom width and height
 * - DPI settings (72, 150, 300)
 * - Transparency toggle (PNG only)
 *
 * **Validates: Requirements 37.1, 37.2, 37.3, 37.4, 37.5, 37.6**
 */

import React, { useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Box,
  Typography,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Image as ImageIcon } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  closeImageExportDialog,
  setImageFormat,
  setImageDimensions,
  setImageDPI,
  togglePreserveTransparency,
  setExportError,
} from '../store/slices/printExportSlice';
import type { ImageFormat, DPI } from '../types/print';

interface ImageExportDialogProps {
  /** Callback to trigger image export */
  onExport?: () => void;
}

const ImageExportDialog: React.FC<ImageExportDialogProps> = ({ onExport }) => {
  const dispatch = useAppDispatch();
  const { imageExportDialogOpen, imageExportOptions, isExporting, exportError } = useAppSelector(
    (state) => state.printExport
  );

  const handleClose = useCallback(() => {
    dispatch(closeImageExportDialog());
  }, [dispatch]);

  const handleFormatChange = useCallback(
    (event: { target: { value: string } }) => {
      dispatch(setImageFormat(event.target.value as ImageFormat));
    },
    [dispatch]
  );

  const handleWidthChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const width = Math.max(1, Number(event.target.value));
      dispatch(setImageDimensions({ width, height: imageExportOptions.height }));
    },
    [dispatch, imageExportOptions.height]
  );

  const handleHeightChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const height = Math.max(1, Number(event.target.value));
      dispatch(setImageDimensions({ width: imageExportOptions.width, height }));
    },
    [dispatch, imageExportOptions.width]
  );

  const handleDPIChange = useCallback(
    (event: { target: { value: string } }) => {
      dispatch(setImageDPI(Number(event.target.value) as DPI));
    },
    [dispatch]
  );

  const handleExport = useCallback(() => {
    if (onExport) {
      onExport();
    }
  }, [onExport]);

  return (
    <Dialog
      open={imageExportDialogOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="image-export-dialog-title"
    >
      <DialogTitle id="image-export-dialog-title">
        <Box display="flex" alignItems="center" gap={1}>
          <ImageIcon />
          <Typography variant="h6">Export Map as Image</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Format */}
          <FormControl fullWidth size="small">
            <InputLabel id="image-format-label">Image Format</InputLabel>
            <Select
              labelId="image-format-label"
              value={imageExportOptions.format}
              label="Image Format"
              onChange={handleFormatChange}
            >
              <MenuItem value="png">PNG (Lossless, supports transparency)</MenuItem>
              <MenuItem value="jpg">JPG (Compressed, smaller file size)</MenuItem>
              <MenuItem value="svg">SVG (Vector, scalable)</MenuItem>
            </Select>
          </FormControl>

          {/* Dimensions */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Image Dimensions (pixels)
            </Typography>
            <Box display="flex" gap={2}>
              <TextField
                label="Width"
                type="number"
                size="small"
                value={imageExportOptions.width}
                onChange={handleWidthChange}
                inputProps={{ min: 100, max: 10000 }}
                fullWidth
              />
              <TextField
                label="Height"
                type="number"
                size="small"
                value={imageExportOptions.height}
                onChange={handleHeightChange}
                inputProps={{ min: 100, max: 10000 }}
                fullWidth
              />
            </Box>
          </Box>

          {/* DPI */}
          <FormControl fullWidth size="small">
            <InputLabel id="image-dpi-label">Resolution (DPI)</InputLabel>
            <Select
              labelId="image-dpi-label"
              value={imageExportOptions.dpi}
              label="Resolution (DPI)"
              onChange={handleDPIChange}
            >
              <MenuItem value={72}>72 DPI (Screen)</MenuItem>
              <MenuItem value={150}>150 DPI (Draft)</MenuItem>
              <MenuItem value={300}>300 DPI (High Quality)</MenuItem>
            </Select>
          </FormControl>

          {/* Transparency (PNG only) */}
          {imageExportOptions.format === 'png' && (
            <FormControlLabel
              control={
                <Switch
                  checked={imageExportOptions.preserveTransparency}
                  onChange={() => dispatch(togglePreserveTransparency())}
                />
              }
              label="Preserve Transparency (Alpha Channel)"
            />
          )}

          {/* Preview info */}
          <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Output: {imageExportOptions.width} × {imageExportOptions.height} px at{' '}
              {imageExportOptions.dpi} DPI
              {imageExportOptions.format === 'png' && imageExportOptions.preserveTransparency
                ? ' (with transparency)'
                : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Print size: {((imageExportOptions.width / imageExportOptions.dpi) * 25.4).toFixed(0)}{' '}
              × {((imageExportOptions.height / imageExportOptions.dpi) * 25.4).toFixed(0)} mm
            </Typography>
          </Box>

          {/* Error display */}
          {exportError && (
            <Alert severity="error" onClose={() => dispatch(setExportError(null))}>
              {exportError}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isExporting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={isExporting}
          startIcon={isExporting ? <CircularProgress size={16} /> : <ImageIcon />}
        >
          {isExporting ? 'Exporting...' : 'Export Image'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageExportDialog;
